import * as RT from '@radix-ui/react-tabs';
import {
  createContext,
  forwardRef,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from 'react';
import { cn } from './cn';

/*
  Tabs — виды одного объекта внутри страницы (уровень навигации 4).
  variant line — подчёркнутые вкладки под шапкой; pill — сегментный переключатель (1 ч · 24 ч · 7 д).
  Намеренно непохожи на WorkspaceTabs (открытые объекты, уровень 3).

  Индикатор (линия или «таблетка» фона) — один элемент, который скользит к активной вкладке:
  положение измеряется по DOM, переезд — duration-smooth · ease-snappy; при первом показе — без анимации;
  prefers-reduced-motion отключает переезд.
*/

type Variant = 'line' | 'pill';
const Ctx = createContext<{ variant: Variant; value?: string }>({ variant: 'line' });

interface TabsProps extends ComponentPropsWithoutRef<typeof RT.Root> {
  variant?: Variant;
}

export function Tabs({
  variant = 'line',
  value,
  defaultValue,
  onValueChange,
  className,
  ...rest
}: TabsProps) {
  const [inner, setInner] = useState<string | undefined>(defaultValue);
  const current = value ?? inner;
  return (
    <Ctx.Provider value={{ variant, value: current }}>
      <RT.Root
        data-slot="tabs"
        value={current}
        onValueChange={(v) => {
          setInner(v);
          onValueChange?.(v);
        }}
        className={cn('flex flex-col', className)}
        {...rest}
      />
    </Ctx.Provider>
  );
}

export const TabsList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof RT.List>>(
  ({ className, children, ...rest }, ref) => {
    const { variant, value } = useContext(Ctx);
    const listRef = useRef<HTMLDivElement | null>(null);
    const [ind, setInd] = useState<{ left: number; width: number; visible: boolean; animate: boolean }>({
      left: 0,
      width: 0,
      visible: false,
      animate: false,
    });

    useLayoutEffect(() => {
      const list = listRef.current;
      if (!list) return;
      const measure = () => {
        const el = list.querySelector<HTMLElement>('[data-state="active"]');
        if (!el) {
          setInd((i) => ({ ...i, visible: false }));
          return;
        }
        const inset = variant === 'line' ? 12 : 0;
        setInd((i) => ({
          left: el.offsetLeft + inset,
          width: Math.max(0, el.offsetWidth - inset * 2),
          visible: true,
          // Первое измерение — без анимации, дальше индикатор едет.
          animate: i.visible,
        }));
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(list);
      return () => ro.disconnect();
    }, [value, variant]);

    return (
      <RT.List
        ref={(node) => {
          listRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        data-slot="tabs-list"
        className={cn(
          'relative flex shrink-0 items-center',
          variant === 'line' ? 'gap-1 border-b border-line' : 'gap-0.5 rounded-md bg-muted p-0.5',
          className,
        )}
        {...rest}
      >
        <span
          aria-hidden
          data-slot="tabs-indicator"
          className={cn(
            'pointer-events-none absolute',
            variant === 'line'
              ? '-bottom-px h-0.5 rounded-full bg-brand-solid'
              : 'top-0.5 bottom-0.5 rounded-sm bg-card shadow-xs',
            ind.animate && 'transition-all duration-smooth ease-snappy motion-reduce:transition-none',
            !ind.visible && 'opacity-0',
          )}
          style={{ transform: `translateX(${ind.left}px)`, width: ind.width }}
        />
        {children}
      </RT.List>
    );
  },
);
TabsList.displayName = 'TabsList';

interface TabsTriggerProps extends ComponentPropsWithoutRef<typeof RT.Trigger> {
  count?: number;
}

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ className, children, count, ...rest }, ref) => {
    const { variant } = useContext(Ctx);
    return (
      <RT.Trigger
        ref={ref}
        data-slot="tabs-trigger"
        className={cn(
          'relative inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap select-none',
          'transition-colors duration-fast focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          variant === 'line'
            ? 'h-9 px-3 text-fg-muted hover:text-fg data-[state=active]:text-fg'
            : 'h-7 rounded-sm px-3 text-fg-muted hover:text-fg data-[state=active]:text-fg',
          className,
        )}
        {...rest}
      >
        {children}
        {typeof count === 'number' && (
          <span className="rounded-full bg-muted px-1.5 text-2xs font-semibold text-fg-muted tabular-nums">
            {count}
          </span>
        )}
      </RT.Trigger>
    );
  },
);
TabsTrigger.displayName = 'TabsTrigger';

export const TabsContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof RT.Content>>(
  ({ className, ...rest }, ref) => (
    <RT.Content
      ref={ref}
      data-slot="tabs-content"
      className={cn('focus-visible:outline-none', className)}
      {...rest}
    />
  ),
);
TabsContent.displayName = 'TabsContent';
